import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import Export from "../../../../components/Export";
import printChart from "../../../../PrintChart";
import Papa from "papaparse";

import DefaultState from "../../../sales-and-transactions/components/DefaultState";
import getCustomerCategoriesCount from "../customer-count/CustomerCategoriesCount";
import InitialPrompt from "../../../../components/InitialPrompt";
import PageHeader from "../../../../components/PageHeader";
import { exportSalesPerSegmentXLSX } from "./exportExcel";

function SalesPerSegmentPage({
  transactions,
  onChangeDate,
  onChangeStore,
  dateField,
  store,
  storess,
  range,
  notFound,
}) {
  const [selectedStore, setSelectedStore] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const getUniqueStores = (transactions) => {
    const storeSet = new Set();
    transactions.forEach((transaction) => {
      const storeCode = transaction.store_code.replace("ANGELSPIZZA_", "");
      storeSet.add(storeCode);
    });
    return Array.from(storeSet);
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      const tranDate = new Date(transaction.tran_date);
      const start = startDate ? new Date(startDate) : new Date(0); // Unix epoch start if empty
      const end = endDate ? new Date(endDate) : new Date();
      end.setHours(23, 59, 59, 999);
      return tranDate >= start && tranDate <= end;
    });
  }, [transactions, startDate, endDate]);

  const uniqueStores = useMemo(
    () => getUniqueStores(filteredTransactions),
    [filteredTransactions],
  );

  // const customerCategories = useMemo(
  //   () => getCustomerCategories(transactions),
  //   [transactions]
  // );
  const customerCategories = useMemo(
    () => getCustomerCategoriesCount(filteredTransactions, selectedStore),
    [filteredTransactions, selectedStore],
  );

  const loyalistCount = customerCategories.loyalists.length;
  const potentialLoyalistCount = customerCategories.potentialLoyalists.length;
  const promisingCount = customerCategories.promising.length;
  const hesitantCount = customerCategories.hesitant.length;
  const needAttentionCount = customerCategories.needAttention.length;
  const detractorsCount = customerCategories.detractors.length;

  const totalCount =
    loyalistCount +
    potentialLoyalistCount +
    promisingCount +
    hesitantCount +
    needAttentionCount +
    detractorsCount;

  const categories = [
    "Loyalist",
    "Potential Loyalist",
    "Promising",
    "Hesitant",
    "Need Attention",
    "Detractors",
  ];

  const colors = [
    "#16a34a",
    "#0891b2",
    "#6366f1",
    "#9333ea",
    "#ea580c",
    "#e11d48",
  ];

  const seriesData = [
    loyalistCount,
    potentialLoyalistCount,
    promisingCount,
    hesitantCount,
    needAttentionCount,
    detractorsCount,
  ];

  const chartOptions = {
    chart: {
      type: "pie",
      style: { fontFamily: "inherit" },
    },
    title: {
      text: "Sales per Segment",
      style: { fontSize: "16px", fontWeight: "bold", color: "#333" },
    },
    tooltip: {
      pointFormat:
        '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.percentage:.2f}%</b> ({point.y})',
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      symbolRadius: 4,
      itemStyle: { fontSize: "12px", fontWeight: "500", color: "#555" },
    },
    plotOptions: {
      pie: {
        innerSize: "55%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          style: { fontWeight: "500", color: "#333", textOutline: "none" },
        },
        showInLegend: true,
      },
    },
    series: [
      {
        name: "Share",
        colorByPoint: true,
        data: categories.map((name, i) => ({
          name: name,
          y: seriesData[i],
          color: colors[i],
        })),
      },
    ],
    credits: { enabled: false },
  };

  const data = {
    labels: categories,
    datasets: [{ data: seriesData }],
  };

  const calculatePercentage = (count) =>
    ((count / totalCount) * 100).toFixed(2);

  // const exportCsv = () => {
  //   const headers = [
  //     "store",
  //     "start_date",
  //     "end_date",
  //     "category",
  //     "sales_percentage",
  //   ];
  //   // Prepare data for CSV
  //   const calculatePercentage = (count) =>
  //     ((count / totalCount) * 100).toFixed(2);
  //   const data = [
  //     {
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Loyalist",
  //       sales_percentage: calculatePercentage(loyalistCount) + "%",
  //     },
  //     {
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Potential Loyalist",
  //       sales_percentage: calculatePercentage(potentialLoyalistCount) + "%",
  //     },
  //     {
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Promising",
  //       sales_percentage: calculatePercentage(promisingCount) + "%",
  //     },
  //     {
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Hesitant",
  //       sales_percentage: calculatePercentage(hesitantCount) + "%",
  //     },

  //     {
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Need Attention",
  //       sales_percentage: calculatePercentage(needAttentionCount) + "%",
  //     },
  //     {
  //       brand: "", // Replace with actual brand name if needed
  //       store: selectedStore,
  //       start_date: startDate || "Not set",
  //       end_date: endDate || "Not set",
  //       category: "Detatractors",
  //       sales_percentage: calculatePercentage(detractorsCount) + "%",
  //     },

  //     // ... [rest of your categories]
  //   ];

  //   // Convert to CSV
  //   const csv = Papa.unparse({
  //     fields: headers,
  //     data: data,
  //   });

  //   // Create a Blob from the CSV String
  //   const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  //   const link = document.createElement("a");
  //   const url = URL.createObjectURL(blob);
  //   link.setAttribute("href", url);
  //   link.setAttribute("download", "customer_categories.csv");
  //   link.style.visibility = "hidden";
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };
  const getDateRange = () => {
    const startRaw = range?.tran_date?.["$gte"] || "";
    const endRaw = range?.tran_date?.["$lte"] || "";
    const start = startRaw ? startRaw.split(" ")[0].split("T")[0] : "N/A";
    const end = endRaw ? endRaw.split(" ")[0].split("T")[0] : "N/A";
    return { start, end };
  };
  const exportXlsx = async () => {
    const { start, end } = getDateRange();
    await exportSalesPerSegmentXLSX({
      brandName: "Angels Pizza",
      storeName: storess,
      startDate: start || "Not set",
      endDate: end || "Not set",
      categories,
      colors,
      counts: seriesData, // <-- your counts array
      fileName: "Sales_Per_Segment.xlsx",
    });
  };

  const chartId = 41;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-1 mt-4">
        <PageHeader
          title="Sales per Segment"
          subtitle="Sales distribution across customer segments"
          icon="bi bi-pie-chart"
        />

        <Export exportCsv={exportXlsx} exportPdf={() => printChart(chartId)} />
      </div>
      <div className="row"></div>

      {transactions.length > 0 ? (
        <div id="41">
          <div className="card">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>
          <div className="print-only-content">
            <h2>Report Details</h2>
            <div className="print-only-content">
              <div>
                <h3>Brand: Angels Pizza</h3>
                <h3>Store: {selectedStore}</h3>
                <h3>
                  Date: {startDate} - {endDate}
                </h3>
              </div>
            </div>
            <div style={{ fontSize: "20px" }}>
              <table className="table text-center">
                <thead>
                  <tr className="d-flex">
                    <th>Type</th>
                    <th className="ms-5">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {data.labels.map((label, index) => (
                    <tr className="d-flex" key={label}>
                      <td>{label}</td>
                      <td>
                        {calculatePercentage(data.datasets[0].data[index])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : !storess || !range ? (
        // <div className="text-center align-items-center mt-4">
        //   <img
        //     src="./choosefirst.png"
        //     style={{ height: "200px", width: "200px" }}
        //   />
        //   <h4 className="mt-4 fw-semibold text-muted text-center">
        //     👋 To get started, please select a{" "}
        //     <span className="text-success">date range</span> and{" "}
        //     <span className="text-success">store</span> above.
        //   </h4>
        // </div>
        <InitialPrompt twoFilter={true} />
      ) : notFound ? (
        <div className="text-center mt-5">
          <p className="text-muted fs-5">
            📭 Data not found for selected filter.
          </p>
        </div>
      ) : (
        <div className="text-center mt-5">
          <DefaultState />
        </div>
      )}
    </>
  );
}

export default SalesPerSegmentPage;
